import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Cart from "./components/cart";
import Payment from "./components/payment";
import {
  generateAndExecuteMutationOrders,
  graphqlQuery,
} from "../../data/Axios/DynamicService";
import Swal from "sweetalert2"; // Import Swal for error alerts
import { AlProducts } from "../../data/Axios/queries";
import { salesManagement, setupManagementUrl } from "../../config";
import { tokens } from "../../theme";

const base_url = setupManagementUrl.uri;
const createOrder_base_url = salesManagement.uri;

const HotelPOS = (onClose) => {
  const [cart, setCart] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [amount, setAmount] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [orderCreated, setOrderCreated] = useState(false);
  const [reaponseTotalAmount, setReaponseTotalAmount] = useState(0);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // State for tracking order creation

  // Fetch products from the GraphQL API

  const fetchProducts = async () => {
    try {
      const result = await graphqlQuery(AlProducts, base_url);
      const nonInventoryProducts = result.filter(
        (product) => !product.isInventory // Filter where isInventory is false
      );
      setProducts(nonInventoryProducts); // Set only the products where isInventory is false
    } catch (error) {
      setSnackbarMessage("Failed to load products");
      setSnackbarOpen(true);
    }
  };
  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle opening the payment dialog
  const handleOpenPaymentDialog = () => {
    if (cart.length > 0) {
      setPaymentDialogOpen(true);
    } else {
      setSnackbarMessage("Cart is empty! Add items to checkout.");
      setSnackbarOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setSelectedProduct(null);
    setOpenDialog(false);
  };

  const handleCreateSaleOrder = async () => {
    try {
      const saleOrderItemsList = cart.map((item) => {
        const saleOrderItem = {
          productId: item.id,
        };

        // Include only one of quantity or amount based on your condition
        if (item.quantity !== undefined && item.quantity !== null) {
          saleOrderItem.quantity = item.quantity; // Include quantity if available
        } else if (item.unitPrice !== undefined && item.unitPrice !== null) {
          saleOrderItem.amount = item.unitPrice * item.quantity; // Include amount if quantity is not present
        }

        return saleOrderItem;
      });

      const inputFields = {
        saleOrderItemsList,
      };
      const returnFields = ["totalAmount"];
      // Add a console log to inspect the response
      const response = await generateAndExecuteMutationOrders(
        "createSaleOrder",
        inputFields,
        null,
        createOrder_base_url,
        returnFields
      );

      // Log the response to inspect the structure

      // Check if response indicates success based on the correct field
      if (response.createSaleOrder) {
        const newOrderId = parseInt(response.createSaleOrder.id); // Extract the orderId

        // Store the orderId in local storage
        localStorage.setItem("orderId", newOrderId);
        setReaponseTotalAmount(response.createSaleOrder.totalAmount);
        setOrderCreated(true); // Set order creation success

        // Optionally, show success message
        setSnackbarMessage(
          "Order created successfully! You can proceed to checkout."
        );
        setSnackbarOpen(true);

        // Open the payment dialog
        handleOpenPaymentDialog();
      } else {
        // If response does not indicate success, handle it here
        throw new Error("Failed to create order. No valid order ID returned.");
      }
    } catch (error) {
      console.error("Error creating sale order:", error);

      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to create the sale order. Please try again.",
      });

      setSnackbarMessage("Failed to create order. Please try again.");
      setSnackbarOpen(true);
    }
  };

  // Handle adding products to the cart
  const handleAddToCart = (item) => {
    if (!quantity && !amount) {
      setSnackbarMessage("Please specify quantity or amount.");
      setSnackbarOpen(true);
      return;
    }

    setCart((prevCart) => {
      const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.id === item.id
            ? {
                ...cartItem,
                quantity: quantity
                  ? parseFloat(quantity)
                  : amount
                  ? parseFloat(amount) / cartItem.unitPrice
                  : cartItem.quantity,
                amount: amount
                  ? parseFloat(amount)
                  : quantity * cartItem.unitPrice,
              }
            : cartItem
        );
      }
      return [
        ...prevCart,
        {
          ...item,
          name: item.productName, // Ensure the product name is included
          unitPrice: item.unitPrice,
          quantity: quantity ? parseFloat(quantity) : amount / item.unitPrice,
          amount: amount ? parseFloat(amount) : quantity * item.unitPrice,
        },
      ];
    });
    setQuantity(1);
    setAmount("");
    setSnackbarMessage(`${item.productName} added to cart.`);
    setSnackbarOpen(true);
    handleCloseDialog();
  };

  // Handle search filtering
  const displayedProducts = products?.filter((product) =>
    product.productName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ padding: 2 }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" gutterBottom>
            POS
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={3} direction={isMobile ? "column" : "row"}>
        <Grid item xs={12} md={6}>
          <Cart
            cart={cart}
            onRemoveItem={(itemId) =>
              setCart(cart.filter((item) => item.id !== itemId))
            }
            onCheckout={handleOpenPaymentDialog}
          />
          <Box sx={{ textAlign: "center" }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ marginTop: 2 }}
              onClick={handleCreateSaleOrder}
            >
              Create Sale Order
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Search Products"
            variant="outlined"
            color="primary"
            fullWidth
            margin="normal"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <Grid container spacing={3}>
            {displayedProducts?.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <Card sx={{ background: `${colors.primary[400]} !important` }}>
                  <CardContent>
                    <Typography variant="h6">{product.productName}</Typography>
                    <Typography variant="body2">
                      Unit Price: KSh {product.unitPrice.toFixed(2)}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "space-between" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setSelectedProduct(product);
                        setOpenDialog(true);
                      }}
                      size="small"
                    >
                      Add to Cart
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Payment
        cart={cart}
        open={paymentDialogOpen}
        totalAmount={reaponseTotalAmount}
        onClose={() => setPaymentDialogOpen(false)}
        onConfirmPayment={() => {
          setCart([]); // Clear cart on payment success
          setPaymentDialogOpen(false);
          setSnackbarMessage("Payment successful! Thank you.");
          setSnackbarOpen(true);
          fetchProducts(); // Refresh product data after successful payment
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Add to Cart</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{selectedProduct?.name}</Typography>
          <TextField
            margin="dense"
            label="Quantity"
            type="number"
            fullWidth
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={() => handleAddToCart(selectedProduct)}>
            Add to Cart
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HotelPOS;
